import createDebug from 'debug';
import { Copy, Loader2, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { authFetch } from '@/api/client';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const debug = createDebug('app:submission-tokens');

// Construct submission URL from token (client-side, always uses current origin)
function getSubmissionUrl(token: string): string {
	return `${window.location.origin}/submit/${token}`;
}

interface PublicToken {
	id: number;
	token: string;
	canRead: boolean;
	canWrite: boolean;
	comment: string | null;
	useCount: number;
	lastUsedAt: number | null;
	expiresAt: number | null;
	createdAt: number;
}

interface SubmissionTokenManagerDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function SubmissionTokenManagerDialog({
	open,
	onOpenChange,
}: SubmissionTokenManagerDialogProps) {
	const [tokens, setTokens] = useState<PublicToken[]>([]);
	const [loading, setLoading] = useState(false);
	const [creating, setCreating] = useState(false);
	const [newComment, setNewComment] = useState('');
	const [deletingId, setDeletingId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [copySuccess, setCopySuccess] = useState<string | null>(null);

	const loadTokens = useCallback(async () => {
		if (!open) return;

		setLoading(true);
		setError(null);
		try {
			const response = await authFetch('/api/queues/0/public-tokens');
			if (!response.ok) {
				const data = await response.json().catch(() => ({}));
				throw new Error(data.error || `Failed to load tokens (${response.status})`);
			}
			const data = await response.json();
			setTokens(data.tokens || []);
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to load tokens';
			setError(message);
			debug('Failed to load tokens: %o', err);
		} finally {
			setLoading(false);
		}
	}, [open]);

	useEffect(() => {
		loadTokens();
	}, [loadTokens]);

	const handleCreate = async () => {
		if (creating) return;

		setCreating(true);
		setError(null);
		try {
			const response = await authFetch('/api/queues/0/public-tokens', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					canRead: false,
					canWrite: true,
					comment: newComment.trim() || undefined,
				}),
			});

			if (!response.ok) {
				const data = await response.json().catch(() => ({}));
				throw new Error(data.error || `Failed to create token (${response.status})`);
			}

			setNewComment('');
			await loadTokens();
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to create token';
			setError(message);
			debug('Failed to create token: %o', err);
		} finally {
			setCreating(false);
		}
	};

	const handleDelete = async (id: number) => {
		if (deletingId !== null) return;

		setDeletingId(id);
		setError(null);
		try {
			const response = await authFetch(`/api/queues/0/public-tokens/${id}`, {
				method: 'DELETE',
			});

			if (!response.ok) {
				const data = await response.json().catch(() => ({}));
				throw new Error(data.error || `Failed to delete token (${response.status})`);
			}

			await loadTokens();
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to delete token';
			setError(message);
			debug('Failed to delete token: %o', err);
		} finally {
			setDeletingId(null);
		}
	};

	const handleCopy = (url: string) => {
		navigator.clipboard.writeText(url);
		setCopySuccess(url);
		setTimeout(() => setCopySuccess(null), 2000);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[60vw] max-w-[800px] max-h-[90vh] p-0 gap-0 overflow-hidden">
				<DialogHeader className="px-6 pt-6 pb-4 border-b">
					<DialogTitle className="text-xl">Submission Tokens</DialogTitle>
					<DialogDescription>
						Manage public submission tokens for your incoming queue. Each token can be shared with
						different groups (family, work, etc.)
					</DialogDescription>
				</DialogHeader>

				<div className="px-6 py-4 space-y-4 overflow-y-auto">
					{/* Error message */}
					{error && (
						<div className="px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
							{error}
						</div>
					)}

					{/* Copy success message */}
					{copySuccess && (
						<div className="px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-md text-sm text-green-600">
							URL copied to clipboard!
						</div>
					)}

					{/* Create new token */}
					<div className="space-y-2">
						<Label htmlFor="comment">Create New Token</Label>
						<div className="flex items-center gap-2">
							<Input
								id="comment"
								placeholder="Comment (e.g., 'Family & Friends', 'Twitter followers')"
								value={newComment}
								onChange={(e) => setNewComment(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										handleCreate();
									}
								}}
								disabled={creating}
							/>
							<Button onClick={handleCreate} disabled={creating}>
								{creating ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
								<span className="ml-2">Create</span>
							</Button>
						</div>
					</div>

					{/* Token list */}
					{loading ? (
						<div className="flex items-center justify-center py-12">
							<Loader2 className="size-6 animate-spin text-muted-foreground" />
							<span className="ml-2 text-sm text-muted-foreground">Loading tokens...</span>
						</div>
					) : tokens.length === 0 ? (
						<div className="text-center py-12 text-muted-foreground">
							<p>No submission tokens yet</p>
							<p className="text-xs mt-1">Create one to allow public submissions</p>
						</div>
					) : (
						<div className="flex flex-col gap-4">
							{tokens.map((token) => (
								<div
									key={token.id}
									className="
										border rounded-lg p-4 bg-background
										grid grid-cols-[auto_1fr] gap-3 items-center
										hover:border-primary hover:shadow-md transition-all
										lg:border-0 lg:border-b lg:rounded-none lg:px-4 lg:py-3
										lg:grid-cols-[1fr_2fr_auto_auto] lg:gap-4
									"
								>
									<div className="col-span-2 lg:col-auto lg:contents">
										<span className="text-sm font-medium m-0 p-0">
											{token.comment || <span className="text-muted-foreground italic">(no comment)</span>}
										</span>
									</div>
									<input
										className="col-span-2 lg:col-auto font-mono text-xs text-muted-foreground bg-muted px-0 lg:px-2 border border-border rounded-md h-9"
										value={getSubmissionUrl(token.token)}
									/>
									<div className="text-xs text-muted-foreground whitespace-nowrap lg:text-right">
										{token.useCount} uses
									</div>
									<div className="flex gap-2 justify-end items-center">
										<Button
											size="sm"
											variant="outline"
											onClick={() => handleCopy(getSubmissionUrl(token.token))}
										>
											<Copy className="size-4 mr-2" />
											Copy URL
										</Button>
										<Button
											size="sm"
											variant="destructive"
											onClick={() => handleDelete(token.id)}
											disabled={deletingId === token.id}
										>
											{deletingId === token.id ? (
												<Loader2 className="size-4 animate-spin" />
											) : (
												<Trash2 className="size-4" />
											)}
										</Button>
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				<div className="px-6 py-4 border-t flex justify-end">
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Close
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
